/// <reference path="./react-table-config.d.ts" />
import { isEqual } from '@deltek/specification-client/core/Utilities/objectUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { usePrevious } from '@dnd-kit/utilities'
import Button from '@material-ui/core/Button'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { CSSProperties } from '@material-ui/core/styles/withStyles'
import Typography from '@material-ui/core/Typography'
import clsx from 'clsx'
import React, { useEffect, useRef } from 'react'
import {
    CellProps,
    Column,
    Filters,
    IdType,
    PluginHook,
    SortingRule,
    TableOptions,
    TableState,
    useColumnOrder,
    useExpanded,
    useFilters,
    useFlexLayout,
    useGroupBy,
    useResizeColumns,
    useRowSelect,
    useSortBy,
    useTable,
} from 'react-table'
import { useVirtual } from 'react-virtual'
import actionColumn from './plugins/actionColumn'
import checkboxColumn from './plugins/checkboxColumn'
import groupedColumn from './plugins/groupedColumn'
import pinnedColumns from './plugins/pinnedColumns'
import useGroupingState from './plugins/useGroupingState'
import { TableSettingsProps } from './settings/TableSettings'
import SpecTableTitleSettings, {
    SpecTableTitleSettingsProps,
} from './SpecTableTitleSettings'
import TableBody from './TableBody'
import TableHeaderFilterRow from './TableHeaderFilterRow'

export const sharedTableStyles = makeStyles((theme: Theme) => ({
    fullWidth: {
        width: '100%',
    },
    textOverflowEllipsis: {
        wordBreak: 'unset',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    cellPadding: {
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(1),
    },
}))
interface specTableStyleProps {
    lazyLoading?: boolean
    isImportTable?: boolean
    tableMinWidth?: string
    groupedCellHeight?: number
}
const specTableStyle = makeStyles((theme: Theme) => ({
    specTable: {
        border: '1px solid #DBDFE0',
        marginBottom: theme.spacing(0),
        '& .tr': {
            '& :last-child': {
                '& .td': {
                    borderBottom: 0,
                },
            },
            '&:first-child': {
                borderTop: '1px solid #C9C9C9',
            },
            '&.grouped-row': {
                height: 35,
            },
            minWidth: (props: specTableStyleProps) =>
                props.tableMinWidth ?? 'auto',
            width: 'fit-content',
        },
        '& .th, & .td': {
            padding: 5,
            wordBreak: 'break-all',
            backgroundColor: theme.palette.common.white,
            overflow: 'hidden',
        },
        '& .td': {
            margin: 'auto',
            height: (props: specTableStyleProps) =>
                props.groupedCellHeight
                    ? pxToRem(props.groupedCellHeight)
                    : 'auto',
        },

        '&.sticky': {
            '& .header': {
                position: 'sticky',
                zIndex: 100,
                top: 0,
            },
            '& .body': (props: specTableStyleProps) => ({
                position: 'relative',
                zIndex: 0,
                height: props.lazyLoading ? 700 : 'inherit',
            }),
            '& [data-sticky-td]': {
                position: 'sticky',
            },
        },
        '&.full-scroll': {
            overflow: 'auto',
        },
        '&.horz-scroll': {
            overflowX: 'auto',
        },
        '&.vertical-scroll': {
            overflowY: 'auto',
            overflowX: 'hidden',
            border: 'none',
        },
        '&.load-next-available': {
            marginBottom: theme.spacing(1),
        },
        '&.no-load-next-available': {
            marginBottom: theme.spacing(1),
        },
    },
    tableBodyContainer: {
        position: 'relative',
    },
    cellPadding: {
        paddingLeft: theme.spacing(1.5),
    },
    loadNextContainer: {
        height: 60,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        bottom: 0,
        width: '100%',
    },
    centerContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 130,
    },
    grayText: {
        ...theme.typography.h1,
        color: theme.palette.grey[700],
    },
    bottomMargin: {
        marginBottom: theme.spacing(1),
    },
}))

const defaultCheckboxColumnWidth = 48

// These are to expose types/interfaces from reac-table since
// we have an imported typings on line 1
export type SpecColumn<TData extends Object> = Column<TData>
export type SpecFilters<TData extends Object> = Filters<TData>
export type SpecIdType<TData extends Object> = IdType<TData>
export type SpecSortingRule<TData> = SortingRule<TData>
export type SpecTableState<TData extends Object> = TableState<TData>

export interface TableFetchDataProps<TData extends Object> {
    sortBy: SortingRule<TData>[]
    groupBy: IdType<TData>[]
    filters?: Filters<TData>
}

export interface TableFetchNextDataProps<TData extends Object>
    extends TableFetchDataProps<TData> {
    totalRows: number
}

export interface ActionColumnProps<TData extends Object> {
    Cell: ({
        cell,
    }: React.PropsWithChildren<CellProps<TData, string>>) => JSX.Element
    width: number
}

export interface SpecTableProps<TData extends Object> {
    actionColumnProps?: ActionColumnProps<TData>
    allowGrouping: boolean
    bodyCellCssOverride?: CSSProperties
    dataLoading: boolean
    divContainerRef: React.MutableRefObject<HTMLDivElement | undefined>
    fetchData: (props: TableFetchDataProps<TData>) => void
    fetchDataLoading: boolean
    projectsLoading?: boolean
    fetchNextData?: (props: TableFetchNextDataProps<TData>) => void
    fetchNextDataLoading?: boolean
    height?: number | string
    onStateChange?: (state: TableState<TData>) => void
    showCheckboxColumn: boolean
    skeletonItemHeight?: number
    tableOptions: TableOptions<TData>
    specTableTitleSettings?: SpecTableTitleSettingsProps<TData>
    totalItems: number
    noDataComponent: React.ReactNode
    lazyLoading?: boolean
    isProjectHome?: boolean
    isImportTable?: boolean
    tableMinWidth?: string
    groupedCellHeight?: number
    tableBodyHeight?: string
    childRef?: any
    showBottomLoadingIndicator?: boolean
}

export const SpecTable = <TData extends Object>({
    actionColumnProps,
    allowGrouping,
    bodyCellCssOverride,
    dataLoading,
    divContainerRef,
    fetchData,
    fetchDataLoading,
    projectsLoading,
    fetchNextData,
    fetchNextDataLoading,
    height,
    onStateChange,
    showCheckboxColumn,
    skeletonItemHeight,
    tableOptions,
    specTableTitleSettings,
    totalItems,
    noDataComponent,
    lazyLoading,
    isProjectHome,
    isImportTable,
    tableMinWidth,
    groupedCellHeight,
    tableBodyHeight,
    childRef,
    showBottomLoadingIndicator,
}: SpecTableProps<TData>) => {
    const classes = specTableStyle({
        lazyLoading: lazyLoading,
        isImportTable: isImportTable,
        tableMinWidth: tableMinWidth,
        groupedCellHeight: groupedCellHeight,
    })

    const plugins: PluginHook<TData>[] = [
        useFilters,
        useFlexLayout,
        useResizeColumns,
        useColumnOrder,
        useGroupBy,
        useSortBy,
        useExpanded,
        useRowSelect,
        (hooks) => {
            // plugin positioning is important here
            hooks.useControlledState.push(useGroupingState)
            hooks.visibleColumns.push(
                pinnedColumns({
                    checkboxColumnWidth: showCheckboxColumn
                        ? defaultCheckboxColumnWidth
                        : 0,
                    showPinning:
                        specTableTitleSettings?.tableSettings?.showPinning ??
                        true,
                    pinningProps: specTableTitleSettings?.tableSettings
                        ?.initialPinningProps ?? {
                        firstColumnPinned: true,
                        lastColumnPinned: true,
                    },
                    actionColumnProps,
                })
            )
            !isImportTable &&
                hooks.visibleColumns.push(
                    groupedColumn(
                        specTableTitleSettings?.tableSettings
                            ?.initialPinningProps?.firstColumnPinned ?? true,
                        specTableTitleSettings?.tableSettings?.canToggleGroup ??
                            true,
                        isProjectHome
                    )
                )
            if (showCheckboxColumn)
                hooks.visibleColumns.push(
                    checkboxColumn(defaultCheckboxColumnWidth)
                )
            if (actionColumnProps) {
                hooks.visibleColumns.push(actionColumn(actionColumnProps))
            }
        },
    ]

    const tableRef = useRef<HTMLDivElement>(null)
    const tableStateRef = useRef<TableState<TData>>()

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        columns,
        setHiddenColumns,
        setColumnOrder,
        toggleAllRowsExpanded,
        isAllRowsExpanded,
        state,
        visibleColumns,
        setAllFilters,
        toggleRowExpanded,
    } = useTable<TData>(tableOptions, ...plugins)

    const rowVirtualizer = useVirtual({
        size: rows.length,
        parentRef: tableRef,
        overscan: 10,
    })

    const prevCount = usePrevious(rows.length)

    const items = rowVirtualizer.virtualItems
    if (rows.length < (prevCount ?? 0)) {
        if (items.length > 0) {
            // need to manually trigger scrollToIndex so the virtualizer will reset the scroll tracker
            rowVirtualizer.scrollToIndex(items[0].index ?? 0)
        }
    }
    const paddingTop = items.length > 0 ? items[0].start : 0
    const paddingBottom =
        items.length > 0
            ? rowVirtualizer.totalSize - items[items.length - 1].end
            : 0

    const tableSettings: TableSettingsProps<TData> = {
        ...specTableTitleSettings?.tableSettings,
        columns,
        allowGrouping,
        initialGroupByColumnIds: state.groupBy,
        initialHiddenColumnIds: state.hiddenColumns ?? [],
        setColumnOrder,
        setHiddenColumns,
        visibleColumns,
    }

    const tableWidth = Number(
        (headerGroups[0].getHeaderGroupProps().style?.width ?? '')
            .toString()
            .replace(/px$/, '')
    )

    const computedWidth =
        tableWidth === 0 ||
        tableWidth > (divContainerRef.current?.offsetWidth ?? 0)
            ? '100%'
            : `${tableWidth}px`

    const handleLoadNext = React.useCallback(() => {
        fetchNextData?.({
            filters: state.filters,
            sortBy: state.sortBy,
            groupBy: state.groupBy,
            totalRows: tableOptions.data.length,
        })
    }, [fetchNextData])

    const handleExpandAllClick = (
        _event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        toggleAllRowsExpanded(true)
    }

    const handleCollapseAllClick = (
        _event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        toggleAllRowsExpanded(false)
    }

    const handleToggleRowExpanded = (rows: Record<string, boolean>) => {
        for (const [key, value] of Object.entries(rows)) {
            toggleRowExpanded([key], value)
        }
    }

    const clearAllFilters = (_event: React.MouseEvent) => {
        setAllFilters([])
    }

    useEffect(() => {
        if (!tableStateRef.current || !isEqual(tableStateRef.current, state)) {
            tableStateRef.current = state
            onStateChange?.(state)
        }
    }, [state, onStateChange])

    useEffect(() => {
        const canToggleGroup =
            specTableTitleSettings?.tableSettings?.canToggleGroup ?? true
        // when group expand collpase is not allowed then group should be in expanded state.
        if (!canToggleGroup && !isAllRowsExpanded && totalItems > 0) {
            toggleAllRowsExpanded(true)
        }
    }, [
        state,
        specTableTitleSettings,
        toggleAllRowsExpanded,
        isAllRowsExpanded,
        totalItems,
    ])

    const isLoadNextAvailable =
        allowGrouping && tableOptions.data.length !== totalItems && !lazyLoading

    if (!!childRef)
        childRef.current = {
            handleToggleRowExpanded: handleToggleRowExpanded,
        }

    return (
        <>
            {!!specTableTitleSettings && (
                <SpecTableTitleSettings
                    {...{
                        hasGrouping: state.groupBy.length > 0,
                        onClearAllFilterClick: clearAllFilters,
                        onCollapseAllClick:
                            handleCollapseAllClick ??
                            toggleAllRowsExpanded(false),
                        onExpandAllClick:
                            handleExpandAllClick ?? toggleAllRowsExpanded(true),
                        ...specTableTitleSettings,
                        tableSettings,
                        width: computedWidth,
                    }}
                />
            )}
            <div
                {...getTableProps()}
                className={clsx(
                    classes.specTable,
                    'sticky',
                    height === 'auto'
                        ? 'horz-scroll'
                        : isImportTable
                        ? 'vertical-scroll'
                        : 'full-scroll',
                    'table'
                )}
                style={{ width: computedWidth, height }}
                ref={tableRef}
            >
                <div className="header">
                    <TableHeaderFilterRow
                        checkboxColumnWidth={
                            showCheckboxColumn ? defaultCheckboxColumnWidth : 0
                        }
                        headerGroups={headerGroups}
                        showFilters={
                            specTableTitleSettings?.showFiltersRow ?? true
                        }
                        isImportTable={isImportTable}
                        clearAllFilters={clearAllFilters}
                    />
                </div>
                <div
                    {...getTableBodyProps()}
                    className={clsx(classes.tableBodyContainer, 'body')}
                >
                    {!fetchDataLoading &&
                    !fetchNextDataLoading &&
                    !(totalItems > rows.length) &&
                    !rows.length ? (
                        <div
                            className={classes.centerContainer}
                            data-testid="noProjectContainer"
                        >
                            <Typography className={classes.grayText}>
                                No Results Found. Try Again.
                            </Typography>
                        </div>
                    ) : (
                        <TableBody
                            projectsLoading={projectsLoading}
                            cellCssOverride={bodyCellCssOverride}
                            checkboxColumnWidth={
                                showCheckboxColumn
                                    ? defaultCheckboxColumnWidth
                                    : 0
                            }
                            fetchDataLoading={
                                fetchDataLoading || !!showBottomLoadingIndicator
                            }
                            fetchNextDataLoading={fetchNextDataLoading}
                            fetchNextPage={handleLoadNext}
                            hideLoadingSkeleton={
                                !fetchDataLoading &&
                                !fetchNextDataLoading &&
                                !(totalItems > rows.length)
                            }
                            pageSize={state.pageSize}
                            prepareRow={prepareRow}
                            rows={rows}
                            skeletonItemHeight={skeletonItemHeight}
                            tableBodyHeight={tableBodyHeight}
                            paddingTop={paddingTop}
                            paddingBottom={paddingBottom}
                            rowVirtualizer={rowVirtualizer}
                        />
                    )}
                </div>
            </div>
            {isLoadNextAvailable && (
                <div
                    className={clsx('tr load-next', classes.loadNextContainer)}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        style={{ width: 200 }}
                        onClick={handleLoadNext}
                        className="load-next-button"
                    >
                        Load Next {state.pageSize}
                    </Button>
                </div>
            )}
            <div className={classes.bottomMargin} />
        </>
    )
}
